/**
 * 对axios的二次封装，将request及response中通用的操作，进行了封装，使用方式同axois
 */
import axios from 'axios';
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  Method,
} from 'axios';
import type { RequestConfig, ResponseData } from './types';
import qs from 'qs';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

NProgress.configure({
  showSpinner: false,
});

class Request {
  // axios实例
  private instance: AxiosInstance;
  // 取消请求集合
  private abortControllers: { [key: string]: AbortController } = {};
  // 配置项
  private _requestConfig: RequestConfig;

  constructor(config: RequestConfig) {
    config.returnStatus =
      config.returnStatus == null ? false : config.returnStatus;
    this._requestConfig = config;
    this.instance = axios.create(config);

    // 默认请求拦截
    this.instance.interceptors.request.use(
      (config: RequestConfig): RequestConfig => {
        if (
          config.method.toLocaleLowerCase() === 'post' &&
          config.headers['Content-Type'] ===
            'application/x-www-form-urlencoded' &&
          typeof config.data === 'object'
        ) {
          // 将json格式的请求体数据，序列化为表单格式数据提交
          config.data = qs.stringify(config.data);
        }

        if (config.method === 'get') {
          // 将请求值为'#all#'的参数过滤掉，这种值用于下拉框中全部选项
          config.params &&
            Object.keys(config.params).forEach(
              (key) =>
                config.params[key] === '#all#' && delete config.params[key]
            );

          // 序列化请求参数中的复杂数据，如：数组、对象等
          config.paramsSerializer = function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          };
        }
        return config;
      },
      (err) => {
        console.error('Request模块：', err);
      }
    );

    // 外部自定义请求拦截
    config.interceptors?.requestInterceptors &&
      this.instance.interceptors.request.use(
        config.interceptors?.requestInterceptors,
        config.interceptors?.requestInterceptorsError
      );

    // 外部自定义响应拦截
    config.interceptors?.responseInterceptors &&
      this.instance.interceptors.response.use(
        config.interceptors?.responseInterceptors,
        config.interceptors?.responseInterceptorsError
      );

    // 默认响应拦截
    this.instance.interceptors.response.use(
      (res: AxiosResponse) => {
        return res;
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  /**
   * 请求函数，最终是调用的axios.request
   * 这里再提供接口粒度的拦截器配置，满足指定接口拦截需求，大部分情况使用全局拦截即可
   * @param config axios配置
   * @returns
   */
  request<T = any, D = ResponseData<T>>(
    config: RequestConfig
  ): Promise<AxiosResponse<D>> {
    config.returnStatus =
      config.returnStatus == null
        ? this._requestConfig.returnStatus
        : config.returnStatus;
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        // 接口请求拦截，使用拦截函数处理config参数
        config = config.interceptors?.requestInterceptors(config);
      }

      // 做取消请求逻辑
      const controller = new AbortController();
      this.abortControllers[`${config.url}-${config.method}`] = controller;

      NProgress.start();
      this.instance
        .request<D>({ signal: controller.signal, ...config })
        .then((res) => {
          if (config.interceptors?.responseInterceptors) {
            // 接口响应拦截，使用拦截函数处理config参数
            res = config.interceptors?.responseInterceptors(res);
          }

          if (config.returnStatus) {
            resolve(res);
          } else {
            resolve(res?.data as any);
          }
        })
        .catch((err: any) => {
          console.error('Request模块：', err);
          reject(err);
        })
        .finally(() => {
          NProgress.done();
          // 请求完毕，删除取消请求集合内容
          delete this.abortControllers[`${config.url}-${config.method}`];
        });
    });
  }

  /**
   * 取消请求
   * @param url 需要取消的请求地址，或者集合
   * @param method 需要取消的请求方法
   */
  cancle(
    url: string | Array<{ url: string; method: Method }>,
    method: Method = 'get'
  ) {
    if (Array.isArray(url)) {
      // 取消集合
      url.forEach((item) => {
        this.abortControllers[`${item.url}-${item.method}`] &&
          this.abortControllers[`${item.url}-${item.method}`].abort();
      });
    } else {
      // 单个取消
      this.abortControllers[`${url}-${method}`] &&
        this.abortControllers[`${url}-${method}`].abort();
    }
  }
}

export default Request;
